/* import __COLOCATED_TEMPLATE__ from './guide-step-component.hbs'; */
/* RESPONSIBLE TEAM: team-self-serve */
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type GreatGuidanceService from 'embercom/services/great-guidance-service';
import type IntlService from 'embercom/services/intl';

import { openTutorial } from 'embercom/helpers/open-tutorial';

interface Args {
  step: any;
  stepAccordion: any;
  isLastStep: boolean;
  currentlyOpenStepId?: string;
}

interface Signature {
  Args: Args;
  Element: never;
}

// Removed platform-dependent step branching; platform handles description key selection

export default class GuideStepComponent extends Component<Signature> {
  @service appService: $TSFixMe;
  @service intercomEventService: $TSFixMe;
  @service router: $TSFixMe;
  @service permissionsService: $TSFixMe;
  @service onboardingHomeService: $TSFixMe;
  @service declare greatGuidanceService: GreatGuidanceService;
  @service declare intl: IntlService;
  @service declare modalService: $TSFixMe;

  @tracked showModal = false;
  @tracked showStandaloneConnectDrawer = false;
  @tracked showInstallMessengerModal = false;

  get app() {
    return this.appService.app;
  }

  get identifier() {
    return this.args.step?.identifier;
  }

  get guideStepTitleTranslationKey(): string {
    return (
      this.appService.mainStandalonePlatform.componentData['onboarding-guide-step']?.titleKey(
        this.identifier,
      ) ?? ''
    );
  }

  get guideStepCtaTranslationKey(): string {
    return (
      this.appService.mainStandalonePlatform.componentData['onboarding-guide-step']?.ctaKey(
        this.identifier,
      ) ?? ''
    );
  }

  get descriptionTranslationKey(): string {
    return (
      this.appService.mainStandalonePlatform.componentData['onboarding-guide-step']?.descriptionKey(
        this.identifier,
      ) ?? ''
    );
  }

  get guideStepSecondaryCtaTranslationKey(): string {
    return (
      this.appService.mainStandalonePlatform.componentData[
        'onboarding-guide-step'
      ]?.secondaryCTAKey(this.identifier) ?? ''
    );
  }

  get stepData() {
    return this.args.step?.stepData;
  }

  get isStepDisabled() {
    return this.identifier === this.args.currentlyOpenStepId;
  }

  get isShopifyGuidanceStep(): boolean {
    return this.identifier?.startsWith('shopify_onboarding_') ?? false;
  }

  get isBlockedByDependency(): boolean {
    if (this.identifier !== 'shopify_onboarding_install_messenger') {
      return false;
    }
    let steps = this.greatGuidanceService.steps;
    if (!steps || steps.length === 0) {
      return false;
    }
    let connectShopifyStep = steps.find(
      (step: any) => step.identifier === 'shopify_onboarding_connect_to_shopify',
    );
    return connectShopifyStep ? !connectShopifyStep.completed : false;
  }

  get blockedByDependencyTooltip(): string {
    return 'onboarding.getting-started.steps.shopify_onboarding_install_messenger.disabled-tooltip';
  }

  get embeddedUrl() {
    if (this.identifier === 'great_guidance_setup_messenger') {
      return `/a/apps/${this.app.id}/settings`;
    }
    return undefined;
  }

  @action
  async closeStandaloneConnectDrawer(shouldFetchSteps = false) {
    this.showStandaloneConnectDrawer = false;
    if (shouldFetchSteps) {
      this.greatGuidanceService.fetchSteps(this.appService.app.id);
    }
  }

  @action
  primaryClickAction() {
    if (this.stepData.primaryAction) {
      this.greatGuidanceService.nextStepId = this.args.step.identifier;
      this.intercomEventService.trackAnalyticsEvent({
        ...this.args.step.analyticsMetadata,
        action: 'clicked',
        context: 'getting_started_homepage',
        object: `${this.identifier}-cta`,
        section: 'onboarding.getting-started',
        place: 'onboarding.getting-started',
        step_state: this.args.step?.state,
      });

      if (this.args.step.identifier === 'great_guidance_invite_teammates' && this.app.onPricing5) {
        if (!this.permissionsService.currentAdminCan('can_manage_teammates')) {
          this.permissionsService.loadAllAdminsAndShowPermissionRequestModal(
            'can_manage_teammates',
          );
        } else {
          this.showModal = true;
        }
      } else if (
        this.appService.mainStandalonePlatform.componentData[
          'onboarding-guide-step'
        ]?.shouldShowConnectDrawer(this.args.step.identifier)
      ) {
        this.showStandaloneConnectDrawer = true;
      } else if (this.args.step.identifier === 'shopify_onboarding_install_messenger') {
        this.showInstallMessengerModal = true;
      } else {
        return this.stepData.primaryAction(this.router, this.args.step, this.app);
      }
    }
  }

  @action
  secondaryClickAction() {
    if (this.stepData.secondaryAction) {
      this.stepData.secondaryAction(this.router, this.args.step, this.app);
      this.intercomEventService.trackAnalyticsEvent({
        ...this.args.step.analyticsMetadata,
        action: 'clicked',
        context: 'getting_started_homepage',
        object: `${this.identifier}-secondary-cta`,
        section: 'onboarding.getting-started',
        place: 'onboarding.getting-started',
        step_state: this.args.step?.state,
      });
    }
  }

  @action
  async closeQuickInviteModal() {
    this.showModal = false;
    await this.onboardingHomeService.loadGuide();
  }

  get shouldShowInstallMessengerModal() {
    // Auto-close modal when step becomes completed (via Nexus event handled by container)
    return this.showInstallMessengerModal && !this.args.step.completed;
  }

  @action
  closeInstallMessengerModal() {
    this.showInstallMessengerModal = false;
  }

  @action
  triggerAnalyticsEventForArticles() {
    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      context: 'getting_started_homepage',
      object: `help_article_link`,
      article_id: this.stepData.articleId,
      step_identifier: this.identifier,
      section: 'onboarding.getting-started',
      place: 'onboarding.getting-started',
    });
  }

  @action
  onMoreInfoFinTutorialClicked() {
    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      context: 'getting_started_homepage',
      object: `help_fin_tutorial_link`,
      article_id: this.moreInfoFinTutorial,
      step_identifier: this.identifier,
      section: 'onboarding.getting-started',
      place: 'onboarding.getting-started',
    });
    openTutorial(this.moreInfoFinTutorial, 'getting-started', 'steps', this.modalService);
  }

  get platform() {
    return (
      this.appService.mainStandalonePlatform.componentData['onboarding-guide-step']?.platformName ??
      ''
    );
  }

  get moreInfoLinkLabel() {
    let platformLabel = this.appService.mainStandalonePlatform.componentData[
      'onboarding-guide-step'
    ]?.moreInfoLinkLabel(this.identifier);
    if (platformLabel) {
      return platformLabel;
    }
    return this.intl.t(`onboarding.getting-started.steps.${this.identifier}.more-info`);
  }

  get moreInfoFinTutorial() {
    return this.stepData?.finTutorial;
  }

  get moreInfoArticleId(): number | undefined {
    return (
      this.appService.mainStandalonePlatform.componentData[
        'onboarding-guide-step'
      ]?.moreInfoArticleId(this.identifier) ?? this.stepData?.articleId
    );
  }

  get hasSecondaryCTA() {
    return this.stepData?.hasSecondaryCTA ?? false;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Onboarding::GettingStarted::Common::GuideStepComponent': typeof GuideStepComponent;
  }
}
